import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Send, Mail, ShieldCheck, ShieldAlert, Eye, EyeOff, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { TestEmailCard } from "@/components/TestEmailCard";
import { AccountSecurity } from "@/components/AccountSecurity";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [days, setDays] = useState(user?.reminder_days_before ?? 5);
  const [currency, setCurrency] = useState(user?.display_currency ?? "INR");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Gmail state
  const [gmailUser, setGmailUser] = useState(user?.gmail_user || "");
  const [gmailPassword, setGmailPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [savingGmail, setSavingGmail] = useState(false);
  const [clearingGmail, setClearingGmail] = useState(false);

  useEffect(() => {
    // Refresh from server to ensure gmail_configured is current
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setGmailUser(user?.gmail_user || "");
    setDays(user?.reminder_days_before ?? 5);
    setCurrency(user?.display_currency ?? "INR");
  }, [user?.gmail_user, user?.reminder_days_before, user?.display_currency]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        reminder_days_before: parseInt(days),
        display_currency: currency,
      });
      await refreshUser();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const saveGmail = async () => {
    if (!gmailUser.trim()) {
      toast.error("Enter your Gmail address");
      return;
    }
    if (!gmailPassword.trim() && !user?.gmail_configured) {
      toast.error("Enter your Gmail App Password");
      return;
    }
    setSavingGmail(true);
    try {
      const payload = { gmail_user: gmailUser.trim() };
      if (gmailPassword.trim()) payload.gmail_app_password = gmailPassword.trim();
      await api.patch("/auth/me", payload);
      await refreshUser();
      setGmailPassword("");
      toast.success("Gmail credentials saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSavingGmail(false);
    }
  };

  const clearGmail = async () => {
    setClearingGmail(true);
    try {
      await api.patch("/auth/me", { clear_gmail: true });
      await refreshUser();
      setGmailUser("");
      setGmailPassword("");
      toast.success("Gmail credentials removed");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setClearingGmail(false);
    }
  };

  const testReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send-now", {});
      if (data.sent > 0) {
        toast.success(`Sent ${data.sent} reminder email(s) to ${user.email}`);
      } else if (!data.gmail_configured) {
        toast.error("Email delivery isn't available right now.");
      } else {
        toast.message("No reminders needed right now (nothing due, overdue, or within your reminder window).");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  const loadDigestPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/reminders/monthly-digest/preview");
      setDigestPreview(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendDigestNow = async () => {
    if (!user?.email_configured) {
      toast.error("Email delivery isn't available right now.");
      return;
    }
    setSendingDigest(true);
    try {
      const { data } = await api.post("/reminders/monthly-digest/send-now", {});
      if (data.sent) {
        toast.success(`Monthly digest sent to ${user.email}`);
      } else {
        toast.error(data.paid_count || data.upcoming_count ? "Could not send the digest — please try again shortly." : "Nothing to include in the digest yet.");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSendingDigest(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Preferences</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Settings</h1>
      </div>

      {/* Reminder preferences */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Bell className="w-4 h-4" />
          <div className="text-sm font-semibold tracking-tight">Reminder preferences</div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notify me — days before due date</Label>
            <Input data-testid="settings-days" type="number" min="0" max="30" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 mono" />
            <p className="text-xs text-slate-500 mt-2">
              You&apos;ll also get emails on the due date and when items become overdue.
              Emails are sent to <span className="mono">{user?.email}</span>.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Display currency (dashboard totals)</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="settings-currency" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR — Indian Rupee</SelectItem>
                <SelectItem value="USD">USD — US Dollar</SelectItem>
                <SelectItem value="EUR">EUR — Euro</SelectItem>
                <SelectItem value="GBP">GBP — British Pound</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">
              Non-native amounts are converted using indicative mid-market rates.
            </p>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white">
              {saving ? "Saving…" : "Save preferences"}
            </Button>
          </div>
        </div>
      </div>

      {/* Gmail delivery */}
      <div className="bg-white border border-slate-200 rounded-md mt-6">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          <div className="text-sm font-semibold tracking-tight">Backup sender — Gmail (optional)</div>
          {user?.gmail_configured ? (
            <span data-testid="gmail-status-configured" className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Configured
            </span>
          ) : (
            <span data-testid="gmail-status-not-configured" className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Not configured
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700 mb-4">
            <div className="font-medium text-slate-900 mb-1">How to get a Gmail App Password (2-min setup)</div>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Enable <b>2-Step Verification</b> on your Google account (myaccount.google.com → Security).</li>
              <li>Open <a className="underline text-zinc-950" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a>.</li>
              <li>Name it <span className="mono">"KMS-Obligo"</span> and generate the 16-character password.</li>
              <li>Paste the Gmail address and the app password below. Your regular Google password will NOT work.</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Gmail address</Label>
              <Input
                data-testid="settings-gmail-user"
                type="email"
                className="mt-1 mono"
                placeholder="you@gmail.com"
                value={gmailUser}
                onChange={(e) => setGmailUser(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Gmail App Password {user?.gmail_configured && <span className="normal-case tracking-normal text-emerald-700">· stored securely</span>}
              </Label>
              <div className="relative mt-1">
                <Input
                  data-testid="settings-gmail-password"
                  type={showPassword ? "text" : "password"}
                  className="mono pr-10"
                  placeholder={user?.gmail_configured ? "Leave blank to keep existing" : "xxxx xxxx xxxx xxxx"}
                  value={gmailPassword}
                  onChange={(e) => setGmailPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  data-testid="settings-gmail-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Password is stored server-side and never returned to the browser. Only you can trigger emails from this account.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-5">
            <Button
              data-testid="settings-gmail-save"
              onClick={saveGmail}
              disabled={savingGmail}
              className="bg-blue-700 hover:bg-blue-800 text-white"
            >
              {savingGmail ? "Saving…" : user?.gmail_configured ? "Update credentials" : "Save Gmail credentials"}
            </Button>
            {user?.gmail_configured && (
              <Button
                data-testid="settings-gmail-clear"
                onClick={clearGmail}
                disabled={clearingGmail}
                variant="outline"
                className="gap-2"
              >
                <Trash2 className="w-4 h-4 text-rose-600" />
                {clearingGmail ? "Removing…" : "Remove"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Send test reminders */}
      <div className="bg-white border border-slate-200 rounded-md mt-6">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Send className="w-4 h-4" />
          <div className="text-sm font-semibold tracking-tight">Send reminders now (test)</div>
        </div>
        <div className="p-5">
          <p className="text-sm text-slate-600">
            Runs the reminder job immediately. Sends only for obligations that fall
            inside your reminder window ({days} days before due date), are due today, or are overdue.
          </p>
          <Button data-testid="send-reminders-now" onClick={testReminders} disabled={sending} className="mt-4" variant="outline">
            {sending ? "Sending…" : "Trigger reminder emails"}
          </Button>
        </div>
      </div>

      {/* Monthly digest */}
      <div className="bg-white border border-slate-200 rounded-md mt-6">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <CalendarDays className="w-4 h-4" />
          <div className="text-sm font-semibold tracking-tight">Monthly digest email</div>
          <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">
            Auto · 1st of every month
          </span>
        </div>
        <div className="p-5">
          <p className="text-sm text-slate-600">
            On the 1st of every month, we email you a summary of what you paid last month and what&apos;s coming up in the
            next 30 days. Sent from the KMS-Obligo mailbox; only sent when there is something to report.
          </p>

          {digestPreview && (
            <div data-testid="digest-preview" className="mt-4 border border-slate-200 rounded-md overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center justify-between">
                <span>
                  Preview · <span className="mono">{digestPreview.period_start}</span> → <span className="mono">{digestPreview.period_end}</span>
                </span>
                <span className="text-[11px] text-slate-500 mono">Currency: {digestPreview.display_currency}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                <div className="p-4">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-700 mb-1">Paid last month</div>
                  <div data-testid="digest-paid-total" className="text-2xl font-semibold mono">
                    {digestPreview.paid_total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{digestPreview.paid_rows.length} payments</div>
                </div>
                <div className="p-4">
                  <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-1">Upcoming next 30 days</div>
                  <div data-testid="digest-upcoming-total" className="text-2xl font-semibold mono">
                    {digestPreview.upcoming_total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{digestPreview.upcoming_rows.length} obligations</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              data-testid="digest-preview-btn"
              onClick={loadDigestPreview}
              disabled={loadingPreview}
              variant="outline"
            >
              {loadingPreview ? "Loading…" : digestPreview ? "Refresh preview" : "Preview my digest"}
            </Button>
            <Button
              data-testid="digest-send-now-btn"
              onClick={sendDigestNow}
              disabled={sendingDigest || !user?.email_configured}
              className="bg-blue-700 hover:bg-blue-800 text-white gap-2"
            >
              <Send className="w-4 h-4" />
              {sendingDigest ? "Sending…" : "Send digest now"}
            </Button>
          </div>
          {!user?.email_configured && (
            <p className="text-xs text-amber-700 mt-2">Email delivery isn't available right now.</p>
          )}
        </div>
      </div>
      {user?.role === "admin" && <TestEmailCard defaultTo={user.email} />}
      <AccountSecurity />
    </div>
  );
}
